import { MiniCalendar } from '@features/appointments';
import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import { Box, Button, Typography } from '@mui/material';

import { useExperimentalCalendarFilters } from '../../context/ExperimentalCalendarFilters';
import { getFixtureProviders } from '../../fixtures';
import { getExtendedColor, providerInitials } from '../../theme/extendedPalette';

export const ExperimentalCalendarSidebar = () => {
  const {
    isProviderSelected,
    toggleProvider,
    selectAllProviders,
    selectOnlyProvider,
    showUnassigned,
    toggleUnassigned,
  } = useExperimentalCalendarFilters();

  const providers = getFixtureProviders();

  return (
    <Box sx={{ py: 2, px: 1 }}>
      {/* MiniCalendar internally uses height: 100%; wrap it so it sizes to its
          natural content rather than filling the sidebar's full height. */}
      <Box sx={{ mb: 3 }}>
        <MiniCalendar />
      </Box>

      <Box sx={{ px: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            mb: 1,
          }}
        >
          <Typography
            variant="overline"
            sx={{ color: ELIG_TOKENS.midnight, fontWeight: 600, letterSpacing: '0.04em' }}
          >
            Providers
          </Typography>
          <Button
            size="small"
            onClick={selectAllProviders}
            sx={{
              minWidth: 'auto',
              p: 0,
              fontSize: 11,
              color: ELIG_TOKENS.inkSoft,
              textTransform: 'none',
            }}
          >
            Show all
          </Button>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {providers.map((p) => {
            const selected = isProviderSelected(p.provider_id);
            const color = getExtendedColor(p.color);
            return (
              <ProviderRow
                key={p.provider_id}
                color={color}
                initials={providerInitials(p.name)}
                name={p.name}
                title={p.title}
                selected={selected}
                onToggle={() => toggleProvider(p.provider_id)}
                onOnly={() => selectOnlyProvider(p.provider_id)}
              />
            );
          })}

          <ProviderRow
            color={ELIG_TOKENS.inkFaint}
            initials="—"
            name="Unassigned"
            title={null}
            selected={showUnassigned}
            onToggle={toggleUnassigned}
          />
        </Box>
      </Box>
    </Box>
  );
};

interface ProviderRowProps {
  color: string;
  initials: string;
  name: string;
  title?: string | null;
  selected: boolean;
  onToggle: () => void;
  onOnly?: () => void;
}

const ProviderRow = ({
  color,
  initials,
  name,
  title,
  selected,
  onToggle,
  onOnly,
}: ProviderRowProps) => {
  return (
    <Box
      onClick={onToggle}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 0.75,
        px: 0.5,
        borderRadius: 1,
        cursor: 'pointer',
        opacity: selected ? 1 : 0.45,
        transition: 'opacity 0.15s, background-color 0.15s',
        '&:hover': { bgcolor: 'rgba(0,0,0,0.03)' },
        '&:hover .provider-row-only': { opacity: 1 },
      }}
    >
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 22,
          height: 18,
          px: 0.5,
          borderRadius: '4px',
          bgcolor: selected ? color : 'transparent',
          color: selected ? '#fff' : color,
          border: selected ? 'none' : `1.5px solid ${color}`,
          fontSize: 10,
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {initials}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontSize: 13,
            color: ELIG_TOKENS.ink,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            lineHeight: 1.2,
          }}
        >
          {name}
        </Typography>
        {title && (
          <Typography
            variant="caption"
            sx={{ fontSize: 10, color: ELIG_TOKENS.inkFaint, lineHeight: 1.2 }}
          >
            {title}
          </Typography>
        )}
      </Box>
      {onOnly && (
        <Button
          className="provider-row-only"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onOnly();
          }}
          sx={{
            opacity: 0,
            transition: 'opacity 0.15s',
            minWidth: 'auto',
            p: 0.25,
            fontSize: 10,
            color: ELIG_TOKENS.inkSoft,
            textTransform: 'none',
          }}
        >
          Only
        </Button>
      )}
    </Box>
  );
};
